<template>
  <div ref="container" class="pdf-viewer-viewport d-flex justify-content-center align-items-start">
    <canvas ref="canvas"></canvas>
  </div>
</template>

<script>
import { cancelRender, destroyDocument, loadDocument, renderPage } from './adapters/pdfjs.adapter'

function normalizeViewportError(error, fallbackCode) {
  const message = error?.message || 'Không thể hiển thị PDF'
  const status = error?.status || error?.statusCode || error?.responseStatus || null
  const code = status === 403 ? 'PDF_SOURCE_EXPIRED' : fallbackCode

  return {
    code,
    message,
    raw: error,
  }
}

export default {
  name: 'PdfViewerViewport',
  props: {
    src: {
      type: String,
      required: true,
    },
    page: {
      type: Number,
      required: true,
    },
    zoomLevel: {
      type: Number,
      required: true,
    },
    rotation: {
      type: Number,
      required: true,
    },
    fitMode: {
      type: String,
      default: 'custom',
    },
  },
  data() {
    return {
      loadingTask: null,
      pdfDoc: null,
      renderTask: null,
      renderRequestId: 0,
      boundResizeHandler: null,
    }
  },
  watch: {
    src: {
      immediate: true,
      handler() {
        this.bootstrap()
      },
    },
    page() {
      this.renderCurrentPage()
    },
    zoomLevel() {
      this.renderCurrentPage()
    },
    rotation() {
      this.renderCurrentPage()
    },
    fitMode() {
      this.renderCurrentPage()
    },
  },
  mounted() {
    this.boundResizeHandler = () => {
      if (this.fitMode !== 'custom' && this.pdfDoc) {
        this.renderCurrentPage()
      }
    }
    window.addEventListener('resize', this.boundResizeHandler)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.boundResizeHandler)
    this.teardown()
  },
  methods: {
    async bootstrap() {
      await this.teardown()

      if (!this.src) return

      try {
        const { loadingTask, pdfDoc } = await loadDocument(this.src)
        this.loadingTask = loadingTask
        this.pdfDoc = pdfDoc
        this.$emit('document-loaded', { totalPages: pdfDoc.numPages })
        await this.renderCurrentPage()
      } catch (error) {
        this.$emit('render-error', normalizeViewportError(error, 'PDF_LOAD_FAILED'))
      }
    },

    async renderCurrentPage() {
      if (!this.pdfDoc || !this.$refs.canvas) return

      const requestId = ++this.renderRequestId
      this.$emit('rendering-state', { rendering: true, page: this.page })
      cancelRender(this.renderTask)

      try {
        const result = await renderPage({
          pdfDoc: this.pdfDoc,
          pageNumber: this.page,
          canvas: this.$refs.canvas,
          scale: this.zoomLevel,
          rotation: this.rotation,
          fitMode: this.fitMode,
          containerWidth: this.$refs.container?.clientWidth || 0,
          containerHeight: this.$refs.container?.clientHeight || 0,
        })

        this.renderTask = result.renderTask
        await result.renderTask.promise
        result.page.cleanup()

        if (requestId !== this.renderRequestId) return

        this.$emit('page-rendered', {
          page: this.page,
          width: result.viewport.width,
          height: result.viewport.height,
          scale: result.scale,
        })
      } catch (error) {
        if (error?.name === 'RenderingCancelledException') {
          return
        }

        this.$emit('render-error', normalizeViewportError(error, 'PDF_RENDER_FAILED'))
      } finally {
        if (requestId === this.renderRequestId) {
          this.$emit('rendering-state', { rendering: false, page: this.page })
        }
      }
    },

    async teardown() {
      cancelRender(this.renderTask)
      this.renderTask = null
      this.renderRequestId += 1

      if (this.$refs.canvas) {
        const context = this.$refs.canvas.getContext('2d')
        context.clearRect(0, 0, this.$refs.canvas.width, this.$refs.canvas.height)
      }

      await destroyDocument({
        loadingTask: this.loadingTask,
        pdfDoc: this.pdfDoc,
      })

      this.loadingTask = null
      this.pdfDoc = null
    },
  },
}
</script>

<style lang="scss" scoped>
.pdf-viewer-viewport {
  min-height: 60vh;
  padding: 0.75rem;
  overflow: auto;
  background: #eff2f5;

  canvas {
    max-width: 100%;
    box-shadow: 0 12px 30px rgba(24, 28, 50, 0.14);
    background: #ffffff;
  }
}
</style>
